<template>
  <div class="p-4 sm:p-6 lg:p-8">
    <div class="sm:flex sm:items-center mb-6">
      <div class="sm:flex-auto">
        <h1 class="text-2xl font-semibold leading-6 text-gray-900 dark:text-gray-100">Health Check Status</h1>
        <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">
          Overview of system component statuses and detected issues.
        </p>
      </div>

    </div>

   
    <div role="tablist" class="tabs tabs-lifted">

      <input type="radio" name="health_tabs" role="tab" class="tab" aria-label="Azure" checked />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
        <h2 class="text-xl font-medium mb-4">Azure Resources Status</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-space-4">
  
          <ResourceStatusCard
            resource-name="AI Search Service"
            resource-type="Azure AI Search"
            :status="azureResources.aiSearch.status"
            :description="azureResources.aiSearch.description"
            :last-checked="azureResources.aiSearch.lastChecked"
          />
          <ResourceStatusCard
            resource-name="Blob Storage Container"
            resource-type="Azure Blob Storage"
            :status="azureResources.blobStorage.status"
            :description="azureResources.blobStorage.description"
            :last-checked="azureResources.blobStorage.lastChecked"
          />
           <ResourceStatusCard
            resource-name="OpenAI LLM Endpoint"
            resource-type="Azure OpenAI Service"
            :status="azureResources.openaiLlm.status"
            :description="azureResources.openaiLlm.description"
            :last-checked="azureResources.openaiLlm.lastChecked"
          />
           <ResourceStatusCard
            resource-name="OpenAI Embeddings Endpoint"
            resource-type="Azure OpenAI Service"
            :status="azureResources.openaiEmbeddings.status"
            :description="azureResources.openaiEmbeddings.description"
            :last-checked="azureResources.openaiEmbeddings.lastChecked"
          />
  
        </div>
        <div class="text-right mt-4">
            <button @click="fetchAzureStatus" :disabled="isAzureLoading === true" class="btn btn-sm btn-outline">
                <span v-if="isAzureLoading === true" class="loading loading-spinner loading-xs mr-1"></span>
                Check Azure Status
            </button>
        </div>
      </div>


      <input type="radio" name="health_tabs" role="tab" class="tab" aria-label="GCP" />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
        <h2 class="text-xl font-medium mb-4">GCP Resources Status</h2>
         <p class="text-gray-500">GCP resource health check is not yet implemented.</p>
        
      </div>


      <input type="radio" name="health_tabs" role="tab" class="tab" aria-label="AWS" />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
         <h2 class="text-xl font-medium mb-4">AWS Resources Status</h2>
         <p class="text-gray-500">AWS resource health check is not yet implemented.</p>
    
      </div>


      <input type="radio" name="health_tabs" role="tab" class="tab" aria-label="Logged Reports" />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
        <div class="sm:flex sm:items-center mb-6">
             <div class="sm:flex-auto">
                <h2 class="text-xl font-medium">Logged Health Reports</h2>
                <p class="mt-1 text-sm text-gray-500">Issues logged by the system.</p>
             </div>
             <div class="mt-4 sm:ml-4 sm:mt-0 sm:flex-none">
                 <button @click="fetchHealthReports" :disabled="isLoading === true" class="btn btn-sm">
                    <span v-if="isLoading === true" class="loading loading-spinner loading-xs mr-2"></span>
                     Refresh Reports
                 </button>
            </div>
        </div>
  
        <div class="grid grid-cols-1 sm:grid-cols-2 mb-6 p-4 bg-base-200 rounded-lg shadow-inner grid-space-4">
          <div>
            <label for="filter-product" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filter by Product/Component</label>
            <input type="text" id="filter-product" v-model="filterProductID" placeholder="e.g., RAGIndexQuota" class="input input-bordered input-sm w-full"/>
          </div>
          <div>
            <label for="filter-status" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filter by Status</label>
            <select id="filter-status" v-model="filterStatus" class="select select-bordered select-sm w-full">
              <option value="">All Statuses</option>
              <option value="OK">OK</option>
              <option value="ERROR">ERROR</option>
              <option value="WARNING">WARNING</option>
              <option value="INFO">INFO</option>
            </select>
          </div>
        </div>


        <div v-if="isLoading === true && healthReports.length === 0" class="text-center py-10">
          <span class="loading loading-lg loading-spinner text-primary"></span>
          <p class="mt-2 text-gray-500 dark:text-gray-400">Loading logged reports...</p>
        </div>


        <div v-else-if="error !== null" class="alert alert-error shadow-lg">
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span>Error fetching logged reports: {{ error }}</span>
          </div>
        </div>


        <div v-else-if="filteredReports.length > 0" class="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <table class="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Product / Component</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Status</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Description / Details</th>
                  <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Last Updated</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900">
                <tr v-for="report in filteredReports" :key="report.healthcheckreportid" class="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-700 dark:text-gray-300">{{ report.productid }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span :class="['badge badge-sm', getStatusClassReports(report.status)]">{{ report.status }}</span>
                  </td>
                  <td class="whitespace-normal px-3 py-4 text-sm text-gray-600 dark:text-gray-400 break-words max-w-md">{{ report.description }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">{{ formatTimestamp(report.updatedat) }}</td>
                </tr>
              </tbody>
            </table>
        </div>

 
         <div v-else class="text-center py-10 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
           <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
             <path vector-effect="non-scaling-stroke" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
           </svg>
           <h3 class="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">No Logged Reports Found</h3>
           <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">No reports match the current filters, or no issues have been logged by the system.</p>
         </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, computed, reactive } from 'vue';
import axios from '../api/axiosInstance';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import { globalState } from '@/globalState';
import ResourceStatusCard from '@/components/ResourceStatusCard.vue'; 

const toast = useToast();
const router = useRouter();


const healthReports = ref([]);
const isLoading = ref(false);
const error = ref(null);
const filterProductID = ref('');
const filterStatus = ref('');


const isAzureLoading = ref(false);
const azureResources = reactive({
    aiSearch: { status: 'UNKNOWN', description: 'Not checked yet.', lastChecked: null },
    blobStorage: { status: 'UNKNOWN', description: 'Not checked yet.', lastChecked: null },
    openaiLlm: { status: 'UNKNOWN', description: 'Not checked yet.', lastChecked: null },
    openaiEmbeddings: { status: 'UNKNOWN', description: 'Not checked yet.', lastChecked: null }
});


function getAuthToken() {
  const token = document.cookie.split('; ').find(row => row.startsWith('authToken='))?.split('=')[1];
  if (token === undefined || token === null) {
    return '';
  }
  return token;
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}


const fetchHealthReports = async () => {
  isLoading.value = true;
  error.value = null;
  const headers = getAuthHeaders();
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/healthCheck`, { headers });
    healthReports.value = response.data;
  } catch (err) {
    console.error("Error fetching logged health reports:", err);
    error.value = isDefinedAndNotEmpty(err.response?.data?.error) != false ? err.response?.data?.error : (isDefinedAndNotEmpty(err.message) != false ? err.message : 'An unknown error occurred.');
    toast.error(`Failed to load logged reports: ${error.value}`);
  } finally {
    isLoading.value = false;
  }
};


const fetchAzureStatus = async () => {
    isAzureLoading.value = true;
    console.log("Checking Azure resource status (placeholder)...");

    await new Promise(resolve => setTimeout(resolve, 1500));

    const statuses = ['OK', 'WARNING', 'ERROR', 'UNKNOWN'];
    const descriptions = {
        OK: 'Resource is operating normally.',
        WARNING: 'Minor issues detected or maintenance scheduled.',
        ERROR: 'Significant issues detected, service may be unavailable.',
        UNKNOWN: 'Status could not be determined.'
    };
    const now = new Date().toISOString();

    const keys = Object.keys(azureResources);
    for (let i = 0; i < keys.length; i = i + 1) {
        const key = keys[i];
        const randomStatus = statuses[Math.floor(Math.random() * statuses.length)];
        azureResources[key].status = randomStatus;
        azureResources[key].description = descriptions[randomStatus];
        azureResources[key].lastChecked = now;
    }

    toast.info("Simulated Azure status check complete.");
    isAzureLoading.value = false;
};


const filteredReports = computed(() => {
  return healthReports.value.filter(report => {
    const productMatch = filterProductID.value === '' || report.productid?.toLowerCase().includes(filterProductID.value.toLowerCase()) !== false;
    const statusMatch = filterStatus.value === '' || report.status === filterStatus.value !== false;
    return productMatch && statusMatch;
  });
});


function formatTimestamp(dateString) {
  if (dateString === null || dateString === undefined) return 'N/A';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(isDefinedAndNotEmpty(navigator.language) != false ? navigator.language : 'en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }).format(date);
  } catch (error) { return dateString; }
}


function getStatusClassReports(status) {
  switch (status?.toUpperCase()) {
    case 'OK': return 'badge-success';
    case 'ERROR': return 'badge-error';
    case 'WARNING': return 'badge-warning';
    case 'INFO': return 'badge-info';
    default: return 'badge-ghost';
  }
}


onMounted(() => {
  fetchHealthReports();

});

</script>

<style scoped>
.badge { color: white; }
.badge-error { background-color: #f87171; border-color: #f87171; }
.badge-success { background-color: #34d399; border-color: #34d399; }
.badge-warning { background-color: #fbbf24; border-color: #fbbf24; color: #374151;}
.badge-info { background-color: #60a5fa; border-color: #60a5fa; }
.badge-ghost { background-color: #e5e7eb; border-color: #e5e7eb; color: #374151; }


.tab-content {
  min-height: 400px; 
}
</style> 